/**
 * Theme controller.
 *
 * We don't use a full state library for this — a zustand-backed store feels
 * heavier than necessary for a two-value enum. This module is:
 *
 *   - A tiny pub/sub so React components can re-render on theme changes.
 *   - A document-side effect that toggles the `dark` class on <html> whenever
 *     the resolved theme changes.
 *
 * Persistence rules:
 *   - On first visit: `preference = 'system'`, follow `prefers-color-scheme`.
 *   - Once the user clicks the toggle: `preference` is set to `'light'` or
 *     `'dark'` and saved to localStorage. Further OS changes are ignored.
 *   - The user can reset to system by calling `setThemePreference('system')`.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'builder:theme';

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Ignore — privacy mode, disabled storage, etc.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

// --- Module-level state + subscribers ---------------------------------------

let preference: ThemePreference = readStoredPreference();
let resolved: ResolvedTheme = resolve(preference);
const subscribers = new Set<() => void>();

function applyToDocument() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

function notify() {
  for (const fn of subscribers) fn();
}

// --- Public API -------------------------------------------------------------

export function getThemePreference(): ThemePreference {
  return preference;
}

export function getResolvedTheme(): ResolvedTheme {
  return resolved;
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  } catch {
    // Ignore storage failures.
  }
  resolved = resolve(next);
  applyToDocument();
  notify();
}

export function subscribeTheme(listener: () => void): () => void {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
}

/**
 * Call once at app startup (before first render) to apply the correct class
 * to <html>. Also wires the system-preference media query so the UI follows
 * OS changes while the user hasn't made an explicit choice.
 */
export function initTheme() {
  applyToDocument();

  if (typeof window === 'undefined') return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (preference !== 'system') return;
    resolved = resolve('system');
    applyToDocument();
    notify();
  };
  // Modern browsers: addEventListener; Safari <14 used addListener.
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
  else mq.addListener(onChange);
}
